import React, { useEffect, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import {
  describeApiError,
  discardInquiryImage,
  submitInquiry,
  uploadInquiryImage,
} from '../services/api';
import type { FulfillmentMode, ItemSource } from '../types';
import { emailError, phoneError } from '../utils/contact';

const ITEM_SOURCE_CHOICES: { value: ItemSource; label: string; description: string }[] = [
  {
    value: 'customer_provided',
    label: "I'll provide the item",
    description:
      "You'll send or drop off the piece you want embroidered (a hat, blanket, towel, etc.).",
  },
  {
    value: 'shop_provided',
    label: 'Please source the item for me',
    description:
      "We'll find and provide the item and add it to your quote (subject to availability).",
  },
  {
    value: 'unsure',
    label: "I'm not sure yet",
    description: "We'll help figure it out when we follow up with your quote.",
  },
];

const MAX_IMAGES = 3;
const MAX_MB = 15;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const ACCEPTED_MIME = /^image\//i;

const FULFILLMENT_CHOICES: {
  value: FulfillmentMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'pickup',
    label: 'Local pickup',
    description: "Collect it in person — we'll arrange a time once it's ready.",
  },
  {
    value: 'shipping',
    label: 'Ship it to me',
    description: 'Postage is added to your quote, and you get a tracking number.',
  },
];

const EMPTY_ADDRESS = { line1: '', line2: '', city: '', state: '', postal_code: '' };

interface AttachedImage {
  file_id: string;
  s3_url: string;
  filename: string;
}

const CustomRequest: React.FC = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [itemSource, setItemSource] = useState<ItemSource>('unsure');
  const [fulfillment, setFulfillment] = useState<FulfillmentMode>('pickup');
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS });
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  // Shown only after a field has been left, so it doesn't scold
  // someone for a half-typed address they're still writing.
  const [touched, setTouched] = useState<{ email?: boolean; phone?: boolean }>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // What the abandon cleanup would throw away, kept in a ref so the unmount
  // handler sees the current list rather than the one from its own render.
  const pending = useRef<string[]>([]);
  pending.current = submitted ? [] : images.map(i => i.file_id);

  const handleFiles = async (filelist: FileList | null) => {
    if (!filelist) return;
    setUploadError(undefined);
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      setUploadError(`Maximum ${MAX_IMAGES} images.`);
      return;
    }
    // Anything past the limit is reported by name rather than silently
    // dropped, so nobody wonders where their fourth photo went.
    const chosen = Array.from(filelist);
    const files = chosen.slice(0, remaining);
    const dropped = chosen.slice(remaining);

    setUploading(true);
    try {
      const uploaded: AttachedImage[] = [];
      for (const file of files) {
        if (!ACCEPTED_MIME.test(file.type)) {
          throw new Error(`${file.name} isn't an image — try a JPG, PNG or WebP.`);
        }
        if (file.size > MAX_BYTES) {
          const mb = (file.size / 1024 / 1024).toFixed(1);
          throw new Error(
            `${file.name} is too big — it's ${mb} MB and the limit is ${MAX_MB} MB per photo.`
          );
        }
        const { file_id, s3_url } = await uploadInquiryImage(file);
        uploaded.push({ file_id, s3_url, filename: file.name });
      }
      setImages(prev => [...prev, ...uploaded]);
      if (dropped.length) {
        setUploadError(
          `Only ${MAX_IMAGES} photos can be attached, so ${dropped
            .map(f => f.name)
            .join(', ')} ${dropped.length === 1 ? 'was' : 'were'} left out.`
        );
      }
    } catch (e: any) {
      setUploadError(describeApiError(e, 'That photo could not be uploaded.'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (file_id: string) => {
    setImages(prev => prev.filter(i => i.file_id !== file_id));
    // The photo is already in the bucket — it goes up the moment it's
    // chosen, so the thumbnail can appear and the submit isn't a long
    // wait. Taking it off the form has to take it out of the bucket too,
    // or it stays there forever with nothing pointing at it.
    discardInquiryImage(file_id);
  };

  /**
   * Clean up photos uploaded for a request that never got sent.
   *
   * Runs when the page is left — navigating away, closing the tab — and
   * only while nothing has been submitted; once the request is in, the
   * photos belong to it and the backend refuses to discard them anyway.
   */
  useEffect(() => {
    const discardPending = () => {
      pending.current.forEach(discardInquiryImage);
      pending.current = [];
    };
    window.addEventListener('pagehide', discardPending);
    return () => {
      window.removeEventListener('pagehide', discardPending);
      discardPending();
    };
  }, []);

  const emailProblem = emailError(email);
  const phoneProblem = phoneError(phone);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailProblem || phoneProblem) {
      setTouched({ email: true, phone: true });
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await submitInquiry({
        customer_name: name,
        customer_email: email,
        customer_phone: phone || undefined,
        message,
        item_source: itemSource,
        fulfillment,
        // Only sent when it's actually wanted — a stale address left behind
        // by switching back to pickup shouldn't travel with the request.
        shipping_address:
          fulfillment === 'shipping'
            ? { ...address, line2: address.line2 || undefined, country: 'US' }
            : undefined,
        image_file_ids: images.length ? images.map(i => i.file_id) : undefined,
      });
      setSubmitted(true);
    } catch (e: any) {
      if (e?.response?.status === 429) {
        setError(
          e?.response?.data?.detail ??
            'You have submitted too many inquiries recently. Please wait 24 hours.'
        );
      } else {
        setError(e?.response?.data?.detail ?? e?.message ?? 'Submission failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold mb-2">Thanks for reaching out!</h1>
        <p className="text-text-light">
          We've received your request and will get back to you within a few
          business days with a quote.
        </p>
      </div>
    );
  }

  const atMax = images.length >= MAX_IMAGES;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-semibold mb-2">Custom order request</h1>
      <p className="text-sm text-text-light mb-6">
        Tell us what you have in mind. Include sizes, colors, and any reference
        images. We'll reply with a quote and a payment link.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, email: true }))}
              aria-invalid={Boolean(touched.email && emailProblem)}
              className={`w-full border rounded px-3 py-2 text-sm ${
                touched.email && emailProblem ? 'border-red-400' : 'border-background-dark'
              }`}
            />
            {touched.email && emailProblem && (
              <p className="text-xs text-red-600 mt-1">{emailProblem}</p>
            )}
          </div>
          <input
            type="text"
            required
            placeholder="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
          <div className="sm:col-span-2">
            <input
              type="tel"
              required
              placeholder="Phone"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, phone: true }))}
              aria-invalid={Boolean(touched.phone && phoneProblem)}
              className={`w-full border rounded px-3 py-2 text-sm ${
                touched.phone && phoneProblem ? 'border-red-400' : 'border-background-dark'
              }`}
            />
            {touched.phone && phoneProblem && (
              <p className="text-xs text-red-600 mt-1">{phoneProblem}</p>
            )}
          </div>
        </div>

        <fieldset className="border border-background-dark rounded p-3 space-y-2">
          <legend className="px-2 text-sm font-medium">Who's supplying the item?</legend>
          {ITEM_SOURCE_CHOICES.map(opt => (
            <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="item_source"
                value={opt.value}
                checked={itemSource === opt.value}
                onChange={() => setItemSource(opt.value)}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-text-light">{opt.description}</div>
              </div>
            </label>
          ))}
        </fieldset>

        <fieldset className="border border-background-dark rounded p-3 space-y-2">
          <legend className="px-2 text-sm font-medium">How would you like it?</legend>
          {FULFILLMENT_CHOICES.map(opt => (
            <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="fulfillment"
                value={opt.value}
                checked={fulfillment === opt.value}
                onChange={() => setFulfillment(opt.value)}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium">{opt.label}</div>
                <div className="text-xs text-text-light">{opt.description}</div>
              </div>
            </label>
          ))}

          {/* Asked here rather than after the quote, so postage can be
              priced into it and nobody has to chase the address later. */}
          {fulfillment === 'shipping' && (
            <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                required
                placeholder="Street address"
                value={address.line1}
                onChange={e => setAddress({ ...address, line1: e.target.value })}
                className="sm:col-span-2 w-full border border-background-dark rounded px-3 py-2 text-sm"
              />
              <input
                placeholder="Apt, suite, etc. (optional)"
                value={address.line2}
                onChange={e => setAddress({ ...address, line2: e.target.value })}
                className="sm:col-span-2 w-full border border-background-dark rounded px-3 py-2 text-sm"
              />
              <input
                required
                placeholder="City"
                value={address.city}
                onChange={e => setAddress({ ...address, city: e.target.value })}
                className="w-full border border-background-dark rounded px-3 py-2 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  required
                  placeholder="State"
                  value={address.state}
                  onChange={e => setAddress({ ...address, state: e.target.value })}
                  className="w-full border border-background-dark rounded px-3 py-2 text-sm"
                />
                <input
                  required
                  placeholder="ZIP"
                  value={address.postal_code}
                  onChange={e => setAddress({ ...address, postal_code: e.target.value })}
                  className="w-full border border-background-dark rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}
        </fieldset>

        <textarea
          required
          rows={6}
          placeholder="Describe your project — sizes, colors, what you'd like embroidered, when you need it…"
          value={message}
          onChange={e => setMessage(e.target.value)}
          className="w-full border border-background-dark rounded px-3 py-2 text-sm"
        />

        <div>
          <label className="block text-xs text-text-light mb-1">
            Reference images (up to {MAX_IMAGES}, {MAX_MB} MB each)
          </label>
          {images.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-2">
              {images.map(img => (
                <div key={img.file_id} className="relative group">
                  <img
                    src={img.s3_url}
                    alt={img.filename}
                    className="w-full h-28 object-cover rounded border border-background-dark"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(img.file_id)}
                    className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    aria-label={`Remove ${img.filename}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* The browser's own file input is styled by the OS and looks
              nothing like the rest of the form, so it's hidden behind a
              button of ours. Outlined rather than filled, to leave "Send
              request" as the only primary action on the page. */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={e => handleFiles(e.target.files)}
            disabled={uploading || atMax}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || atMax}
            className="inline-flex items-center gap-2 border border-primary text-primary rounded px-4 py-2 text-sm font-medium hover:bg-primary/5 disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <Upload size={16} />
            {uploading ? 'Uploading…' : 'Choose photos'}
          </button>
          <div className="text-xs text-text-light mt-1">
            {uploading
              ? 'Uploading…'
              : atMax
                ? `Maximum ${MAX_IMAGES} images attached.`
                : `${images.length} / ${MAX_IMAGES} attached.`}
          </div>
          {uploadError && <div className="text-sm text-red-600 mt-1">{uploadError}</div>}
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <button
          type="submit"
          disabled={busy || uploading}
          className="bg-primary text-white rounded px-5 py-3 text-sm font-medium disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send request'}
        </button>
      </form>
    </div>
  );
};

export default CustomRequest;
